import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { UsersPageWrapper } from 'src/components/profileComponents/Users/components/UsersPageWrapper';
import { ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import { translations } from 'src/constants/translations';
import { pathList } from 'src/containers/Profile/routeList';
import { useParams } from 'react-router-dom';
import { useDebounce, useGetComplex } from 'src/hooks';
import { IComplexUser } from 'src/typings/user';
import { UserInput } from 'src/components/profileComponents/Users/components/UserInput';
import InputAdornment from '@mui/material/InputAdornment';
import { appIcon } from 'src/assets';
import { ButtonApp } from 'src/components/ApartmentPageV2/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AppTable } from 'src/components/AppTable';
import { useComplexUsers } from 'src/components/profileComponents/EditComplex/hooks/useComplexUsers';
import { useTable } from 'src/hooks/table/useTable';
import { Pagination } from '@mui/material';
import { toQueryString } from 'src/helpers/buildQueryParamsString';
import { GridColDef } from '@mui/x-data-grid';
import { useStyles as useCommonUsers } from 'src/components/profileComponents/Common/Users';
import {
  complexUsersTableRows,
  EditComplexUsersAssignType,
  EditComplexUsersQueryType, ISelectedUsers,
} from 'src/components/profileComponents/EditComplex/constants';
import { RolesMap } from 'src/components/profileComponents/Users/constants';
import Checkbox from '@mui/material/Checkbox';
import clsx from 'clsx';
import { useToastr } from 'src/hooks/useToastr';
import { ComplexUserInfoTag } from 'src/components/profileComponents/EditComplex/components/ComplexUserInfoTag';
import { queryClient } from 'src/lib/queryClient';
import { Response } from 'src/api/hydraResponse';
import { useGlobalGrid } from 'src/hooks/app/use-global-grid';
import { ButtonSwitcher } from 'src/components/profileComponents/Common/ButtonSwitcher';
import { useStyles } from './styles';

const initData = {
  [EditComplexUsersAssignType.notAssigned]: [],
  [EditComplexUsersAssignType.assigned]: [],
};

export const ComplexUsers: React.FC = () => {
  const { id }: { id: string } = useParams();
  const { complex, fetchComplex } = useGetComplex(id);
  const [page, setPage] = useState<number | undefined>(1);
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<ISelectedUsers>(initData);
  const [assignedState, setAssignedState] = useState<EditComplexUsersAssignType>(EditComplexUsersAssignType.notAssigned);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(1);
  const {
    apiRef,
    rows,
    columns,
    setRows,
    setColumns,
  } = useTable();
  const {
    complexUsersData, complexUsersDataIsLoading, refetch, setGetParams, saveComplexUsers, assignAllUsers,
  } = useComplexUsers();
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const commonUsersStyles = useCommonUsers();
  const { toastrClear, toastrSuccess, toastrError } = useToastr();
  const { getGridsWidth } = useGlobalGrid();
  const styles = useStyles(getGridsWidth);

  useEffect(() => {
    if (complex) {
      setGetParams(toQueryString({
        [EditComplexUsersQueryType[assignedState]]: [complex.id.toString()],
        developers: [complex?.developers[0].id],
        search: debouncedSearch.trim().toString().toLowerCase(),
        page: page?.toString() || '',
        properties: {
          0: '@id',
          1: 'id',
          2: 'username',
          3: 'status',
          4: 'role',
          5: 'email',
          6: 'complexes',
        },
      }));
    }
  }, [complex, assignedState, debouncedSearch, page]);

  useEffect(() => {
    const tempColumns: GridColDef[] = complexUsersTableRows.map((field) => ({
      field: field.id.toString(),
      headerName: field.headerName,
      type: 'string',
      headerAlign: field.headerAlign,
      align: field.align,
      flex: field.flex,
      // minWidth: getGridsWidth(field.width),
      // maxWidth: getGridsWidth(field.width),
      display: 'flex',
      renderHeader: (params) => {
        return (
          <div className={commonUsersStyles.text}>
            {params.field === '1' && (
              <Checkbox
                className={commonUsersStyles.checkbox}
                color="primary"
                checked={isAllChecked}
                onChange={() => selectAllUsers()}
              />
            ) }
            {params.colDef.headerName}
          </div>
        );
      },
      renderCell: (params) => {
        const fieldValue = params.row[field.fieldName];
        switch (field.fieldName) {
          case 'username':
            return (
              <>
                <Box display="flex" alignItems="center" gap="12px">
                  <Checkbox
                    className={commonUsersStyles.checkbox}
                    color="primary"
                    checked={!!isChecked(params.row)}
                    onChange={() => toggleUser(params.row)}
                  />
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap="4px">
                    <span className={commonUsersStyles.text}>{fieldValue}</span>
                    <span className={commonUsersStyles.subText}>{params.row.email}</span>
                  </Box>
                </Box>
              </>
            );
          case 'role':
            return <div>{params.row.role?.name || ''}</div>;
          case 'status':
            return (
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap="8px">
                {isNotAssignedState && (
                  <span className={`${styles.statusText} ${styles.statusTextNotAssigned}`}>Not assigned</span>
                ) }
                { isAssignedState && (
                  <span className={`${styles.statusText} ${styles.statusTextAssigned}`}>Assigned</span>
                ) }
              </Box>
            );
          default: return <Box display="flex" alignItems="flex-start">{fieldValue}</Box>;
        }
      },
    })) || [];
    setColumns(tempColumns);
    // @ts-ignore
    setRows(complexUsersData?.['hydra:member'] || []);
    setTotalItems(complexUsersData?.['hydra:totalItems'] || 0);
    setItemsPerPage(complexUsersData?.['hydra:itemsPerPage'] || 1);
  }, [complexUsersData, selectedUsers]);

  useEffect(() => {
    fetchComplex();
  }, []);

  const usersList = useMemo<IComplexUser[]>(() => selectedUsers?.[assignedState] || [], [selectedUsers, assignedState]);

  const isChecked = useCallback((user: IComplexUser) => {
    return usersList.find((usr) => usr?.id === user.id);
  }, [usersList]);

  const isAllChecked = useMemo(() => {
    return usersList.length === complexUsersData?.['hydra:member'].length;
  }, [usersList, complexUsersData]);

  const toggleUser = useCallback((user: IComplexUser) => {
    const tmpUsers: IComplexUser[] = [...usersList];
    if (isChecked(user)) {
      const index = tmpUsers.findIndex((usr) => usr?.id === user.id);
      tmpUsers.splice(index, 1);
    } else {
      tmpUsers.push({
        '@context': user['@context'],
        '@id': user['@id'],
        '@type': user['@type'],
        id: user.id,
      });
    }
    setSelectedUsers({ ...selectedUsers, [assignedState]: tmpUsers });
  }, [usersList, selectedUsers, assignedState, isChecked]);

  const selectAllUsers = useCallback(() => {
    let data: ISelectedUsers;
    if (complexUsersData?.['hydra:member'].length === selectedUsers[assignedState].length) {
      data = { ...selectedUsers, [assignedState]: [] };
    } else {
      data = { ...selectedUsers, [assignedState]: complexUsersData?.['hydra:member'] || [] };
    }
    setSelectedUsers(data);
  }, [complexUsersData, selectedUsers, assignedState]);

  const isDataExist = useMemo(() => complexUsersData && complexUsersData?.['hydra:member'].length > 0, [complexUsersData]);
  const usersCount = useMemo(() => (complexUsersData && complexUsersData?.['hydra:totalItems']) || 0, [complexUsersData]);
  const isAssignedState = useMemo(() => EditComplexUsersAssignType.assigned === assignedState, [EditComplexUsersAssignType, assignedState]);
  const isNotAssignedState = useMemo(() => EditComplexUsersAssignType.notAssigned === assignedState, [EditComplexUsersAssignType, assignedState]);
  const isSubmitBtnDisabled = useMemo(() => !isDataExist || usersList.length === 0 || complexUsersDataIsLoading || submitInProgress, [isDataExist, usersList, complexUsersDataIsLoading, submitInProgress]);
  const isAssignAllBtnDisabled = useMemo(() => !isDataExist || complexUsersDataIsLoading || submitInProgress, [isDataExist, usersList, complexUsersDataIsLoading, submitInProgress]);

  const afterRequest = <T, >(res: Response<T>) => {
    toastrClear();
    if (!res.ok) {
      toastrError();
      setSubmitInProgress(false);
      return;
    }
    queryClient.clear();
    refetch()
      .then(() => {
        setSelectedUsers(initData);
        setSubmitInProgress(false);
        toastrSuccess();
      });
  };

  const onSubmit = async () => {
    if (complex) {
      setSubmitInProgress(true);
      const tmpComplex = { users: complex.users as IComplexUser[] };
      if (isNotAssignedState) {
        tmpComplex.users = [...tmpComplex.users].concat(usersList) as IComplexUser[];
      }
      if (isAssignedState) {
        const usersIds = usersList.map((user) => user?.id);
        tmpComplex.users = tmpComplex.users.filter((user) => !usersIds.includes(user.id)) as IComplexUser[];
      }
      const res = await saveComplexUsers(complex.id, tmpComplex);
      afterRequest(res);
    }
  };

  const assignAll = async () => {
    if (complex) {
      setSubmitInProgress(true);
      const res = await assignAllUsers(complex.id);
      afterRequest<void>(res);
    }
  };

  const CustomPagination = () => {
    const pageCount = Math.ceil(totalItems / itemsPerPage);
    let node: React.ReactNode = <div className="currentPage">{ `Page ${pageCount} of ${pageCount}` }</div>;

    return (
      <Box sx={{ p: 1 }}>
        { !!rows.length && (
          <Pagination
            count={pageCount}
            siblingCount={0}
            boundaryCount={-2}
            defaultPage={1}
            page={page || 1}
            onChange={(e, value) => {
              if (value > 0) {
                setPage(value);
              }
            }}
            renderItem={(params) => {
              if (
                params.type === 'start-ellipsis' || params.type === 'end-ellipsis') {
                return null;
              }
              if (params.type === 'page' && params.selected) {
                node = <div className="currentPage">{ `Page ${page || 1} of ${pageCount}` }</div>;
              } else {
                node = '';
              }
              return (
                <>
                  { params.type === 'previous' && (
                    <Box
                      className={`chevron chevron-left ${params.disabled ? 'disabled' : ''}`}
                      style={{
                        background: `url(${appIcon.iconChevronRightThin.default}) 55% center no-repeat`,
                      }}
                      onClick={(e) => {
                        if (!params.disabled) params.onClick(e);
                      }}
                    />
                  ) }
                  {node}
                  { params.type === 'next' && (
                    <Box
                      className={`chevron chevron-right ${params.disabled ? 'disabled' : ''}`}
                      style={{
                        background: `url(${appIcon.iconChevronRightThin.default}) 55% center no-repeat`,
                      }}
                      onClick={(e) => {
                        if (!params.disabled) params.onClick(e);
                      }}
                    />
                  ) }
                </>
              );
            }}
            color="standard"
          />
        ) }
      </Box>
    );
  };

  const crumbs: ICrumbs[] = [
    {
      name: translations.complexesLink,
      url: `/profile/${pathList.complexes}`,
    },
    {
      name: `${translations.settings} ${complex?.title[complex?.language] || ''}`,
      url: `/profile/${pathList.complexes}/${id}`,
    },
    {
      name: translations.manageProjectUsersLink,
    },
  ];
  return (
    <UsersPageWrapper
      crumbs={crumbs}
      title={`MANAGE ${complex?.title[complex?.language] || ''} USERS`}
    >
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.search}>
            <div className="search">
              <UserInput
                startAdornment={(
                  <InputAdornment position="start">
                    <img src={appIcon.iconSearch.default} alt="icon search" />
                  </InputAdornment>
                )}
                placeholder={translations.managersSearchPlaceholder}
                name="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <ButtonSwitcher
            firstBtn={{
              name: 'Not assigned',
              state: isNotAssignedState,
              click: () => setAssignedState(EditComplexUsersAssignType.notAssigned),
            }}
            secondBtn={{
              name: 'Assigned',
              state: isAssignedState,
              click: () => setAssignedState(EditComplexUsersAssignType.assigned),
            }}
          />
          <div className={styles.assignActions}>
            <div className={`select-all ${commonUsersStyles.secondaryBtnWrap}`}>
              {isNotAssignedState && (
                <ButtonApp
                  className={`secondary-btn ${clsx(
                    isAssignAllBtnDisabled && 'disabled',
                  )}`}
                  disabled={isAssignAllBtnDisabled}
                  onClick={assignAll}
                >
                  { `Assign all ${usersCount} users` }
                </ButtonApp>
              ) }
            </div>
            <ButtonApp
              type="submit"
              className={`btn ${clsx(
                isAssignedState && 'assign',
                isNotAssignedState && 'not-assign',
                isSubmitBtnDisabled && 'disabled',
              )}`}
              disabled={isSubmitBtnDisabled}
              onClick={onSubmit}
            >
              { (complexUsersDataIsLoading || submitInProgress) ? (
                <CircularProgress size={24} />
              ) : (
                <span>
                  {`${isNotAssignedState ? 'Assign' : 'Remove'} ${usersList.length} of ${usersCount}`}
                </span>
              )}
            </ButtonApp>
          </div>
        </div>
        {!complexUsersDataIsLoading && (
        <div className={styles.info}>
          {!!search.length && !isDataExist && (
            <ComplexUserInfoTag label="No users found" />
          )}
          {!search.length && (
            <>
              { isNotAssignedState && (
              <>
                { !isDataExist ? (
                  <ComplexUserInfoTag label="No unassigned users. Looks like everyone’s already on board 🚀" />
                ) : (
                  <ComplexUserInfoTag
                    label="Select one or more users using the checkboxes to assign them from the project:"
                  />
                ) }
              </>
              ) }
              { isAssignedState && (
              <>
                { !isDataExist ? (
                  <ComplexUserInfoTag
                    label="No assigned users. It’s a bit quiet here… maybe you should assign someone 😎"
                  />
                ) : (
                  <ComplexUserInfoTag
                    label="Select one or more users using the checkboxes to remove them from the project:"
                  />
                ) }
              </>
              ) }
            </>
          ) }
        </div>
        ) }
        <div className={commonUsersStyles.tableSection}>
          { complexUsersDataIsLoading
            ? (
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                { isDataExist && (
                <AppTable
                  slots={{
                    pagination: CustomPagination,
                  }}
                  autoHeight
                  autosizeOnMount
                  apiRef={apiRef}
                  disableColumnFilter
                  disableColumnMenu
                  disableColumnSorting
                  disableColumnResize
                  disableRowSelectionOnClick
                  columns={columns}
                  rows={rows}
                  rowHeight={76}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-container--top [role=row]': {
                      background: 'unset',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      padding: 0,
                    },
                    '& .MuiDataGrid-columnHeader': {
                      padding: 0,
                    },
                  }}
                />
                )}
              </>
            ) }
        </div>
      </div>
    </UsersPageWrapper>
  );
};
